import { useEffect, useState } from 'react'
import { useReducedMotion } from '../kit'
import { useTooltip } from './Tooltip'

// One whole in parts: a 14px bar with a 2px surface gap between segments and a
// legend under it naming every part and its count. Status colours are right
// here because the parts are states (cleared, pending, blocked), and each
// segment opens the list it counts.

export type Part = { key: string; label: string; count: number; color: string; href?: string }

// legend false leaves the key to the caller, for a card that lists the parts
// as links with their counts (Home's risk card) and would otherwise say each
// count twice.
export function StackBar({ parts, title, legend = true }: { parts: Part[]; title: string; legend?: boolean }) {
  const reduced = useReducedMotion()
  const tip = useTooltip()
  const [grown, setGrown] = useState(reduced)
  const total = parts.reduce((a, p) => a + p.count, 0)

  useEffect(() => {
    if (reduced) {
      setGrown(true)
      return
    }
    const t = window.setTimeout(() => setGrown(true), 200)
    return () => window.clearTimeout(t)
  }, [reduced])

  const label = title + ': ' + parts.map((p) => p.label + ' ' + p.count).join(', ')
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div className="hc-stack" role="group" aria-label={label}>
        {parts
          .filter((p) => p.count > 0)
          .map((p, i) => {
            const style = {
              background: p.color,
              width: grown && total > 0 ? (p.count / total) * 100 + '%' : 0,
              transitionDelay: i * 120 + 'ms',
            }
            const rows = [{ label: p.label, value: String(p.count), color: p.color }]
            const events = {
              onPointerMove: (e: React.PointerEvent) => tip.show(e, title, rows),
              onPointerLeave: () => tip.hide(),
              onFocus: (e: React.FocusEvent) => tip.showAt(e.currentTarget, title, rows),
              onBlur: () => tip.hide(),
            }
            return p.href ? (
              <a key={p.key} href={p.href} style={style} aria-label={p.label + ': ' + p.count} {...events} />
            ) : (
              <span key={p.key} style={style} tabIndex={0} aria-label={p.label + ': ' + p.count} {...events} />
            )
          })}
      </div>
      {legend && (
        <div className="hc-stackleg">
          {parts.map((p) => (
            <span key={p.key}>
              <i style={{ background: p.color }} />
              {p.label} {p.count}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
